/**
 * Règles partagées du SMS d'annonce de départ au client.
 *
 * Elles ne servent qu'à décider de l'affichage (bouton, ouverture de la fenêtre) :
 * c'est l'API (DeliveryClientSmsService) qui tranche réellement à l'envoi.
 */

/**
 * Numéro de mobile français (06 / 07), quelle que soit la saisie (espaces, points, +33…).
 * Même règle que DeliveryClientSmsService::isMobile().
 */
export const isMobile = (phone) => {
  if (!phone) return false;
  let digits = String(phone).replace(/\D/g, "");
  if (digits.startsWith("0")) digits = `33${digits.slice(1)}`;
  return /^33[67]\d{8}$/.test(digits);
};

export const hasMobile = (roadmapDelivery) =>
  isMobile(roadmapDelivery?.delivery?.phone) ||
  isMobile(roadmapDelivery?.delivery?.phone_secondary);

/**
 * Un arrêt peut-il recevoir le SMS ?
 * - pas une récupération : le message annonce un départ de cuisine ;
 * - un mobile joignable ;
 * - pas déjà prévenu : un seul envoi par arrêt.
 */
export const canSendClientSms = (roadmapDelivery) =>
  canResendClientSms(roadmapDelivery) && !roadmapDelivery.client_sms_sent_at;

/**
 * Un renvoi manuel est-il possible ?
 *
 * Mêmes conditions, sans le verrou « déjà prévenu » : depuis la feuille de livraison le
 * livreur peut renvoyer délibérément — client qui dit n'avoir rien reçu, créneau qui a
 * bougé. Le chemin automatique, lui, reste en un seul coup.
 */
export const canResendClientSms = (roadmapDelivery) =>
  Boolean(roadmapDelivery) &&
  !roadmapDelivery.recovery &&
  hasMobile(roadmapDelivery);

/**
 * La prochaine livraison de la tournée, après l'arrêt courant et dans l'ordre de passage.
 *
 * Les récupérations sont ignorées : elles ne reçoivent pas ce SMS (rien ne quitte les
 * cuisines pour elles) et elles ne doivent pas non plus masquer le client à prévenir.
 * Sur une tournée LIV A → RECUP X → LIV B, confirmer A vise donc bien B.
 */
export const getNextDeliveryToWarn = (roadmap, currentRoadmapDelivery) => {
  if (!roadmap?.deliveries || !currentRoadmapDelivery) return null;

  const ordered = [...roadmap.deliveries].sort(
    (a, b) => (a.sort ?? 0) - (b.sort ?? 0)
  );
  const index = ordered.findIndex(
    (item) => parseInt(item.id, 10) === parseInt(currentRoadmapDelivery.id, 10)
  );

  if (index === -1) return null;

  return ordered.slice(index + 1).find((item) => !item.recovery) || null;
};

/**
 * Identité de l'arrêt destinataire : société, contact, numéro de prestation.
 *
 * Indispensable quand la fenêtre s'ouvre à la confirmation de la livraison précédente —
 * le livreur écrit alors à un client qui n'est pas celui affiché à l'écran.
 */
export const getClientSummary = (roadmapDelivery) => {
  const delivery = roadmapDelivery?.delivery;
  if (!delivery) return null;

  return {
    company: delivery.company || delivery.info?.companyName || null,
    contact: [delivery.firstname, delivery.lastname].filter(Boolean).join(" ") || null,
    numbering: delivery.info?.numbering || null,
  };
};
